import IconComponent from "@CubeComponents/icons";

/**
 * One watched folder as the desktop-sync bridge reports it. `index` is attached locally by
 * `buildSpaceHierarchyMap` so pause/resume/cancel can address the entry.
 */
type SyncItem = {
  spaceId?: string;
  hierarchyId?: string;
  watchedPath?: string;
  status?: string;
  index?: number;
  [key: string]: unknown;
};

/** `fileSyncState` is either the flat entry list or the bridge's `{ data: { state } }` envelope. */
type FileSyncStateLike = SyncItem[] | { data?: { state?: SyncItem[] } } | null;

/** `spaceId -> hierarchyId -> { data: entries }`. */
type SpaceSyncMap = Record<string, Record<string, { data: SyncItem[] }>>;

/** The subset of the Electron preload bridge this panel drives. */
type ElectronSyncApi = {
  pauseFileSync: (args: { index: number }) => void;
  resumeFileSync: (args: { index: number }) => void;
  cancelFileSync: (args: { index: number }) => void;
  initLocalFoldersSync: (args: Record<string, unknown>) => void;
};
import { useTranslation, Trans, untranslated } from "@i18n";
import Popover from "@mui/material/Popover";
import { useState } from "react";
import { maximiseFileSync } from "@Reducers/fileSync";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { CircularProgress, Link, ButtonBase } from "@mui/material";
import _ from "lodash";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import RemoveSyncModal from "../FilesSync/RemoveSyncModal";
import { RootState } from "@Reducers/index";
import { FILE_SYNC_MODULE } from "@Actions/filesync";
function FolderSyncTemplate({
  spaceId,
  projectId,
  spaceData,
  currentFolder,
}: {
  spaceId: string;
  projectId: string;
  spaceData: { spaceName?: string };
  currentFolder: { hierarchyId?: string; name?: string };
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  function handleClick(event: React.MouseEvent<HTMLElement>) {
    if (countOfLinkFiles == 0) {
      initLocalFoldersSync();
    } else {
      setAnchorEl(event.currentTarget);
    }
  }

  const handleCloseOverlay = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  // Explicit type argument: the initialiser alone infers `data: null`, but the confirm handlers store
  // the sync entry's index here. The initial value is unchanged.
  const [removeSync, setRemoveSync] = useState<{ modal: boolean; data: number | null }>({
    modal: false,
    data: null,
  });
  const dispatch = useDispatch();
  const fileSyncState = useSelector((state: RootState) => state.fileSyncState);
  const fileSyncMapperState = useSelector((state: RootState) => state.fileSyncMapperState);
  const portalData = useSelector((state: RootState) => state.portalData);
  const userDetails = useSelector((state: RootState) => state.userDetails);

  function buildSpaceHierarchyMap(input: FileSyncStateLike): SpaceSyncMap {
    let items: SyncItem[] = [];
    if (Array.isArray(input)) {
      items = input;
    } else if (input?.data?.state && Array.isArray(input.data.state)) {
      items = input.data.state;
    }

    const res: SpaceSyncMap = {};
    for (let i = 0; i < items.length; i++) {
      let raw = items[i];
      if (!raw || typeof raw !== "object") continue;

      const spaceId = String(raw.spaceId || "");
      const hierarchyId = String(raw.hierarchyId || "");
      if (!spaceId || !hierarchyId) continue;

      if (!res[spaceId]) {
        res[spaceId] = {};
      }
      if (!res[spaceId]![hierarchyId]) {
        res[spaceId]![hierarchyId] = { data: [] };
      }
      res[spaceId]![hierarchyId]!.data.push({ ...raw, index: i });
    }
    return res;
  }

  const handlePause = (index: number) => {
    (window as Window & { electronAPI?: ElectronSyncApi }).electronAPI!.pauseFileSync({ index });
  };
  const handleResume = (index: number) => {
    (window as Window & { electronAPI?: ElectronSyncApi }).electronAPI!.resumeFileSync({ index });
  };
  const handleCancel = (index: number) => {
    (window as Window & { electronAPI?: ElectronSyncApi }).electronAPI!.cancelFileSync({ index });
  };

  const spaceMap = buildSpaceHierarchyMap(fileSyncState);
  const spaceFileSyncData = spaceMap[spaceId] || {};

  const countOfLinkFiles = Object.values(spaceFileSyncData)
    .flatMap((o: { data: SyncItem[] }) => o.data)
    .filter((d: SyncItem) => d.status !== "CANCELLED").length;

  function initLocalFoldersSync() {
    (window as Window & { electronAPI?: ElectronSyncApi }).electronAPI!.initLocalFoldersSync({
      spaceId,
      spaceName: spaceData.spaceName,
      path: currentFolder.name,
      projectId: projectId,
      hierarchyId: currentFolder.hierarchyId,
      origin: window.location.origin,
      portalId: portalData._id,
      userId: userDetails._id,
    });
  }

  const renderSyncItem = (item: SyncItem) => {
    if (item.status === "CANCELLED") return null;

    return (
      <div className="flex flex-row items-center gap-6 py-1 justify-between" key={item.watchedPath}>
        <div className="flex flex-row items-center gap-6 overflow-auto">
          <IconComponent name="desktop" width="1.5rem" height="1.5rem" />
          <div className="overflow-hidden truncate">
            <ToolTipComponent content={untranslated(item.watchedPath || "")}>
              <div className="overflow-hidden truncate">{item.watchedPath}</div>
            </ToolTipComponent>
          </div>
        </div>
        <div className="flex flex-row items-center justify-center">
          {item.status === "IN_PROGRESS" && (
            <div className="flex group relative w-6 h-6 justify-center items-center">
              <div className="group-hover:hidden flex justify-center items-center">
                <CircularProgress size={16} thickness={6} />
              </div>
              <div
                aria-label="Pause Sync"
                className="hidden group-hover:flex items-center justify-center cursor-pointer"
              >
                <IconComponent
                  name="PauseIcon"
                  onClick={() => handlePause(item.index!)}
                  width="1.25rem"
                  height="1.25rem"
                />
              </div>
            </div>
          )}

          {item.status === "PAUSED" && (
            <div className="flex justify-center flex-row items-center gap-6">
              <IconComponent
                name="Start"
                aria-label="Resume Sync"
                className="cursor-pointer"
                onClick={() => handleResume(item.index!)}
                width="1rem"
                height="1rem"
              />
              <IconComponent
                name="Reject"
                aria-label="Remove Sync"
                className="cursor-pointer"
                onClick={() => setRemoveSync({ modal: true, data: item.index! })}
                width="1rem"
                height="1rem"
              />
            </div>
          )}

          {item.status === "ERRORED" && (
            <div className="flex justify-center flex-row items-center gap-6">
              <IconComponent
                name="Error"
                toolTipContent={t("Something went wrong while adding remote file")}
                width="1.25rem"
                height="1.25rem"
              />
              <IconComponent
                name="Reject"
                aria-label="Remove Sync"
                className="cursor-pointer"
                onClick={() => setRemoveSync({ modal: true, data: item.index! })}
                width="1rem"
                height="1rem"
              />
            </div>
          )}

          {(item.status === "COMPLETED" || !item.status) && (
            <div className="group relative w-6 h-6 flex justify-center items-center">
              <div className="group-hover:hidden">
                <IconComponent name="TickRight" width="1.25rem" height="1.25rem" />
              </div>
              <div
                aria-label="Remove Sync"
                className="hidden group-hover:flex items-center justify-center cursor-pointer"
              >
                <IconComponent
                  name="Reject"
                  onClick={() => setRemoveSync({ modal: true, data: item.index! })}
                  width="1rem"
                  height="1rem"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <ButtonBase aria-describedby={id} className="flex gap-2 flex-row p-1" onClick={handleClick}>
        {countOfLinkFiles === 0 ? (
          <>
            <IconComponent name="desktop" width="1rem" height="1rem" />
            <div className="font-semibold text-sm">{t("Sync this Folder")}</div>
          </>
        ) : (
          <>
            <IconComponent name="consume" width="1rem" height="1rem" />
            <div className="font-semibold text-sm">{t("Syncing with PC")}</div>
            <div className="rounded-full bg-button_light font-semibold text-sm w-6 h-6 flex truncate items-center justify-center">
              {countOfLinkFiles}
            </div>
          </>
        )}
      </ButtonBase>
      <Popover
        className="z-10"
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseOverlay}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <div className="w-120 max-h-200 flex">
          <div className="flex flex-col gap-6 p-6 overflow-auto w-full">
            <div className="flex flex-row justify-between items-center">
              <ButtonComponent onClick={initLocalFoldersSync}>
                <div className="flex flex-row gap-2 items-center">
                  <IconComponent name="add" width="1rem" height="1rem" />
                  <div>{t("New")}</div>
                </div>
              </ButtonComponent>
              <Link
                underline="hover"
                className="text-accentBlue font-semibold cursor-pointer text-sm"
                onClick={() => {
                  handleCloseOverlay();
                  dispatch(maximiseFileSync());
                }}
              >
                {t("View All Syncs")}
              </Link>
            </div>

            <div className="flex flex-col gap-6 overflow-auto">
              {Object.keys(spaceFileSyncData).map((key: string) => {
                const currSpaceData = spaceFileSyncData[key];
                const spaceDataLength = _.get(currSpaceData, "data", []).filter(
                  (item: SyncItem) => item.status !== "CANCELLED",
                ).length;

                const folderName =
                  key === "0"
                    ? spaceData.spaceName
                    : _.get(fileSyncMapperState, `${FILE_SYNC_MODULE.HIERARCHY_ID}[${key}].data.filterPath`, "")
                        .split("/")
                        .pop();

                if (spaceDataLength === 0) return null;

                return (
                  <div className="flex flex-col gap-6" key={key}>
                    <div className="flex flex-row items-center gap-2">
                      <IconComponent name="folder" width="1.5rem" height="1.5rem" />
                      <Trans
                        i18nKey="folder_sync_with"
                        values={{
                          folderName,
                        }}
                        components={{
                          div1: <div style={{ fontSize: "1rem", fontWeight: "bold" }} />,
                          div2: <div style={{ fontSize: "1rem" }} />,
                        }}
                      />
                    </div>
                    <div className="flex flex-col gap-6 border-button_light border rounded-lg px-4 py-6">
                      {_.get(currSpaceData, "data", []).map(renderSyncItem)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <RemoveSyncModal removeSync={removeSync} setRemoveSync={setRemoveSync} handleCancel={handleCancel} />
        </div>
      </Popover>
    </>
  );
}

export default FolderSyncTemplate;
